import base64 from "base-64";

// DEV-1483 — helper murni untuk "Buat Order dari Undangan (AI)" (DEV-1478).

// Penanda teks form order manual (sama dengan Global.FORM_ORDER_MESSAGE_MARK & be-new FORM_ORDER_MARK).
export const FORM_ORDER_MARK = "Untuk pemesanan, silahkan lengkapi data";

export const DETECTION_STATUS = {
    PROCESSING: "PROCESSING",
    DETECTED: "DETECTED",
    FAILED: "FAILED",
    ORDER_CREATED: "ORDER_CREATED",
    INVOICE_SENT: "INVOICE_SENT",
    INVOICE_FAILED: "INVOICE_FAILED",
};

// Id provinsi DKI Jakarta di master geo. Pencarian produk Sakura tanpa kota hanya punya fallback
// untuk DKI (kota-kota Jakarta); provinsi lain tanpa kota menampilkan produk semua kota (DEV-1484).
export const DKI_PROVINCE_ID = 1642907;

// Lokasi siap untuk mencari/menyimpan SKU: provinsi terisi, dan kota terisi kecuali DKI.
export const isLocationReady = (provinceId, cityId) => !!provinceId && (!!cityId || provinceId === DKI_PROVINCE_ID);

const URL_REGEX = /https?:\/\/[^\s<>"'`]+/i;

const fileExt = (name) => {
    const clean = String(name || "").split(/[?#]/)[0];
    return clean.includes(".") ? clean.split(".").pop().toLowerCase() : "";
};

/**
 * Format pesan yang bisa dibaca AI — sama dengan classifyMessage() di be-new
 * (modules/invitation/utils/invitationContent.ts): IMAGE, DOCUMENT PDF, TEXT form order,
 * TEXT berisi link. Stiker, suara, video, lokasi, kontak, dan pesan Tele sendiri -> null.
 */
export const getInvitationSourceType = (data, isMine) => {
    if (isMine || !data?.message) return null;
    const message = data.message;
    const type = String(message.type || "").toUpperCase();

    if (type === "IMAGE") return "IMAGE";
    if (type === "DOCUMENT") {
        const mime = String(message.mimeType || "").toLowerCase();
        const isPdf = mime.includes("pdf") || fileExt(message.fileName) === "pdf" || fileExt(message.objectKey) === "pdf";
        return isPdf ? "PDF" : null;
    }
    if (type === "TEXT" || type === "") {
        const text = message.text || "";
        if (text.toLowerCase().includes(FORM_ORDER_MARK.toLowerCase())) return "FORM_ORDER";
        return URL_REGEX.test(text) ? "LINK" : null;
    }
    return null;
};

/** Semua user yang login boleh memakai fitur ini, tanpa whitelist role. */
export const canCreateInvitationOrder = (session) => !!session?.user;

export const SOURCE_TYPE_LABELS = {
    IMAGE: "Gambar",
    PDF: "PDF",
    LINK: "Link undangan",
    FORM_ORDER: "Teks form order",
};

// Urutan & label ringkasan data hasil AI di popup "Undangan Terdeteksi".
export const RESULT_FIELDS = [
    { key: "receiverName", label: "Penerima" },
    { key: "eventDate", label: "Tanggal acara" },
    { key: "eventTime", label: "Jam acara" },
    { key: "shippingAddress", label: "Alamat" },
    { key: "city", label: "Kota" },
    { key: "occasion", label: "Jenis acara" },
    { key: "eventSummary", label: "Ringkasan acara" },
    { key: "senderName", label: "Nama pengirim" },
    { key: "greetings", label: "Ucapan" },
    { key: "receiverPhone", label: "Nomor penerima" },
    { key: "notes", label: "Catatan khusus" },
    { key: "customerEmail", label: "Email" },
];

const MISSING_FIELD_LABELS = {
    receiverName: "Nama penerima",
    eventDate: "Tanggal acara",
    eventTime: "Jam acara",
    shippingAddress: "Alamat",
    province: "Provinsi",
    city: "Kota",
    occasion: "Jenis acara",
    senderName: "Nama pengirim",
    greetings: "Ucapan",
    receiverPhone: "Nomor penerima",
};

export const missingFieldLabel = (key) => MISSING_FIELD_LABELS[key] || key;

/** Baris ringkasan hasil AI yang terisi saja. Kota memakai nama master (cityName/provinceName). */
export const summarizeResult = (result) => {
    if (!result) return [];
    return RESULT_FIELDS.map(({ key, label }) => {
        let value = result[key];
        if (key === "city") {
            value = [result.cityName || result.city, result.provinceName || result.province].filter(Boolean).join(", ");
        }
        return { key, label, value: value ? String(value) : "" };
    }).filter((row) => row.value);
};

/**
 * Chip status di bawah pesan (Skenario 13) + tombol yang tersedia.
 * tone: success | error | info | pending.
 */
export const describeDetectionStatus = (detection) => {
    if (!detection) return null;
    const invoiceSending = detection.invoice?.status === "SENDING";
    const order = detection.orderNumber;

    switch (detection.status) {
        case DETECTION_STATUS.PROCESSING:
            return { label: "AI sedang membaca undangan…", tone: "pending", detail: null, actions: [] };
        case DETECTION_STATUS.DETECTED:
            return { label: "Deteksi berhasil", tone: "success", detail: null, actions: ["continue"] };
        case DETECTION_STATUS.FAILED:
            return { label: "Deteksi gagal", tone: "error", detail: detection.failedMessage || null, actions: ["retry", "withoutAi"] };
        case DETECTION_STATUS.ORDER_CREATED:
            return {
                label: `Order dibuat — ${order}`,
                tone: "info",
                detail: invoiceSending ? "Invoice sedang dikirim…" : null,
                actions: [],
            };
        case DETECTION_STATUS.INVOICE_SENT:
            return {
                label: "Invoice terkirim",
                tone: "success",
                detail: invoiceSending ? `Order ${order} · Invoice sedang dikirim ulang…` : `Order ${order}`,
                actions: invoiceSending ? [] : ["resendInvoice"],
            };
        case DETECTION_STATUS.INVOICE_FAILED:
            return {
                label: "Invoice gagal terkirim",
                tone: "error",
                detail: invoiceSending ? `Order ${order} · Invoice sedang dikirim ulang…` : detection.invoice?.failedMessage || `Order ${order}`,
                actions: invoiceSending ? [] : ["resendInvoice"],
            };
        default:
            return null;
    }
};

/** Host form Sakura sesuai lingkungan Lotus (sama dengan tombol Create Order Link). */
export const getSakuraWebUrl = (appMode) =>
    appMode === "PRODUCTION" ? "https://sakura.prestisa.id" : "https://stg-sakura.prestisa.id";

/** Form Create Order Sakura dari hasil deteksi: /enquiries/<b64 {phone, detection_id}> (DEV-1481). */
export const buildInvitationOrderUrl = (appMode, phone, detectionId) =>
    `${getSakuraWebUrl(appMode)}/enquiries/${base64.encode(JSON.stringify({ phone, detection_id: detectionId }))}`;

export const buildSakuraOrderUrl = (appMode, orderNumber) => `${getSakuraWebUrl(appMode)}/orders/view/${orderNumber}`;

export const formatRupiah = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "-";
    return "Rp " + Math.round(n).toLocaleString("id-ID");
};

/** Deteksi per pesan: { [waMessageId]: ringkasan } dari GET /invitation-detections. */
export const indexDetectionsByMessage = (detections) => {
    const map = {};
    (detections || []).forEach((d) => {
        if (d?.waMessageId && !map[d.waMessageId]) map[d.waMessageId] = d;
    });
    return map;
};
